import React, { useState, useEffect } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, VStack, HStack, Box, Text, Radio, RadioGroup, Checkbox, Textarea, Badge,
  FormControl, FormLabel, useToast, Divider, Collapse, UnorderedList, ListItem,
} from "@chakra-ui/react";
import { registrarCrmTarefaDesfecho } from "../../services/api";
import {
  EIXO_ESFORCO, EIXO_CONTATO, EIXO_OBJETIVO, DESDOBRAMENTOS, OBJETIVOS_ABERTURA,
  CANAIS_TOQUE, tarefaTipoInfo,
} from "./crmUtils";

const ACCENT = "#F68633";

/** Um eixo do desfecho: título, explicação e as opções em rádio. */
function Eixo({ numero, titulo, ajuda, opcoes, valor, onChange }) {
  return (
    <Box>
      <HStack spacing={2} mb={1}>
        <Badge colorScheme="orange" borderRadius="full">{numero}</Badge>
        <Text fontWeight="semibold" fontSize="sm">{titulo}</Text>
      </HStack>
      {ajuda && <Text fontSize="xs" color="gray.500" mb={2}>{ajuda}</Text>}
      <RadioGroup value={valor || ""} onChange={onChange} colorScheme="orange">
        <VStack align="start" spacing={1}>
          {opcoes.map((o) => (
            <Radio key={o.value} value={o.value} size="sm">
              <Text fontSize="sm">{o.label}</Text>
            </Radio>
          ))}
        </VStack>
      </RadioGroup>
    </Box>
  );
}

/**
 * Registro do desfecho em três eixos (V1.7). Os eixos aparecem em cascata: o
 * contato só faz sentido se tentou, e o objetivo só se o cliente respondeu.
 * A 1.1 ganha ainda a captura parcial dos seus 4 objetivos.
 *
 * `info` é a entrada do catálogo (o "como usar" da tarefa): fica aqui como
 * lembrete dobrável, para o vendedor conferir abordagem e cuidados sem sair da tela.
 */
export default function DesfechoTarefaModal({ isOpen, onClose, tarefa, info, onSaved }) {
  const [esforco, setEsforco] = useState("");
  const [canal, setCanal] = useState("ligacao");
  const [contato, setContato] = useState("");
  const [objetivo, setObjetivo] = useState("");
  const [desdobramento, setDesdobramento] = useState("");
  const [objetivos, setObjetivos] = useState({});
  const [obs, setObs] = useState("");
  const [verComoUsar, setVerComoUsar] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setEsforco(""); setCanal("ligacao"); setContato(""); setObjetivo("");
      setDesdobramento(""); setObjetivos({}); setObs(""); setVerComoUsar(false);
    }
  }, [isOpen]);

  const tipo = tarefaTipoInfo(tarefa?.tipo);
  const ehAbertura = tarefa?.tipo === "abertura";
  const cuidados = Array.isArray(info?.cuidados) ? info.cuidados : [];
  const desdobramentoInfo = DESDOBRAMENTOS.find((d) => d.value === desdobramento);

  // Cascata: sem tentativa não há contato; sem resposta não há objetivo.
  const respondeu = esforco === "tentou" && contato === "respondido";
  const podeSalvar = esforco === "nao_tentou"
    || (esforco === "tentou" && !!canal && (contato === "nao_respondido" || (respondeu && !!objetivo)));

  const salvar = async () => {
    setSalvando(true);
    const r = await registrarCrmTarefaDesfecho(tarefa.id, {
      esforco,
      canal: esforco === "tentou" ? canal : null,
      contato: esforco === "tentou" ? contato : null,
      objetivo: respondeu ? objetivo : null,
      desdobramento: respondeu && desdobramento ? desdobramento : null,
      obs: obs.trim() || null,
      objetivos: ehAbertura ? objetivos : undefined,
    });
    setSalvando(false);

    if (r?.data) {
      toast({
        status: "success",
        title: "Desfecho registrado",
        description: (r.efeitos || []).join(" "),
        duration: 6000,
      });
      onSaved?.();
      onClose();
    } else {
      toast({ status: "error", title: "Erro", description: r?.error || "Não foi possível registrar." });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          Registrar desfecho
          {tipo && <Badge ml={2} colorScheme="orange" variant="outline">{tipo.codigo}</Badge>}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Box bg="gray.50" borderRadius="md" px={3} py={2}>
              <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{tarefa?.cliente_nome || "Cliente"}</Text>
              <Text fontSize="xs" color="gray.500">{tarefa?.motivo}</Text>
              {Number(tarefa?.tentativas) > 0 && (
                <Text fontSize="xs" color="orange.600" mt={1}>
                  {tarefa.tentativas}ª tentativa sem resposta já registrada.
                </Text>
              )}
            </Box>

            {info && (
              <Box>
                <Button size="xs" variant="link" colorScheme="purple" onClick={() => setVerComoUsar((v) => !v)}>
                  {verComoUsar ? "Ocultar como fazer esta tarefa" : "Relembrar como fazer esta tarefa"}
                </Button>
                <Collapse in={verComoUsar} animateOpacity>
                  <Box bg="purple.50" borderRadius="md" px={3} py={2} mt={2}>
                    {info.canal && <Text fontSize="xs" color="gray.600"><b>Canal:</b> {info.canal}</Text>}
                    {info.abordagem && <Text fontSize="xs" color="gray.600" mt={1}><b>Abordagem:</b> {info.abordagem}</Text>}
                    {cuidados.length > 0 && (
                      <>
                        <Text fontSize="xs" color="gray.600" mt={1}><b>Cuidados:</b></Text>
                        <UnorderedList spacing={0.5} pl={4}>
                          {cuidados.map((c, i) => <ListItem key={i}><Text fontSize="xs" color="gray.600">{c}</Text></ListItem>)}
                        </UnorderedList>
                      </>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}

            <Eixo
              numero="Eixo 1"
              titulo="Esforço"
              ajuda="Mede o seu esforço. Não ter tentado mantém a tarefa pendente."
              opcoes={EIXO_ESFORCO}
              valor={esforco}
              onChange={(v) => { setEsforco(v); setContato(""); setObjetivo(""); setDesdobramento(""); }}
            />

            {esforco === "tentou" && (
              <>
                <Divider />
                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Por onde você falou</FormLabel>
                  <RadioGroup value={canal} onChange={setCanal} colorScheme="orange">
                    <HStack spacing={4}>
                      {CANAIS_TOQUE.map((c) => (
                        <Radio key={c.value} value={c.value} size="sm">
                          <Text fontSize="sm">{c.label}</Text>
                        </Radio>
                      ))}
                    </HStack>
                  </RadioGroup>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    O toque entra como atividade na ficha e conta nas metas do dia.
                  </Text>
                </FormControl>
                <Eixo
                  numero="Eixo 2"
                  titulo="Contato"
                  ajuda="Sem resposta a tarefa é reagendada; esgotadas as tentativas, a conta esfria."
                  opcoes={EIXO_CONTATO}
                  valor={contato}
                  onChange={(v) => { setContato(v); setObjetivo(""); setDesdobramento(""); }}
                />
              </>
            )}

            {respondeu && (
              <>
                <Divider />
                <Eixo
                  numero="Eixo 3"
                  titulo="Objetivo"
                  ajuda={`O objetivo da tarefa foi cumprido? Não cumprido devolve a conta para a cadência.${info?.cumprido ? ` Nesta tarefa, cumprido = ${info.cumprido.charAt(0).toLowerCase()}${info.cumprido.slice(1)}` : ""}`}
                  opcoes={EIXO_OBJETIVO}
                  valor={objetivo}
                  onChange={setObjetivo}
                />

                {ehAbertura && (
                  <Box>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      Captura parcial: marque os objetivos que você conseguiu cobrir nesta conversa.
                    </Text>
                    <VStack align="start" spacing={1}>
                      {OBJETIVOS_ABERTURA.map((o) => (
                        <Checkbox
                          key={o.value}
                          size="sm"
                          colorScheme="orange"
                          isChecked={!!objetivos[o.value]}
                          onChange={(e) => setObjetivos((p) => ({ ...p, [o.value]: e.target.checked }))}
                        >
                          <Text fontSize="sm">{o.label}</Text>
                        </Checkbox>
                      ))}
                    </VStack>
                  </Box>
                )}

                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Desdobramento (opcional)</FormLabel>
                  <RadioGroup value={desdobramento} onChange={setDesdobramento} colorScheme="orange">
                    <VStack align="start" spacing={1}>
                      {DESDOBRAMENTOS.map((d) => (
                        <Radio key={d.value} value={d.value} size="sm">
                          <Text fontSize="sm">
                            {d.label}{" "}
                            <Text as="span" fontSize="xs" color={d.encadeia ? "green.600" : "gray.400"}>
                              — {d.efeito}
                            </Text>
                          </Text>
                        </Radio>
                      ))}
                    </VStack>
                  </RadioGroup>
                  {desdobramento && (
                    <Button size="xs" variant="link" colorScheme="gray" mt={2} onClick={() => setDesdobramento("")}>
                      limpar desdobramento
                    </Button>
                  )}
                  {desdobramentoInfo && !desdobramentoInfo.encadeia && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Fica guardado no histórico — a tarefa de destino entra na próxima leva.
                    </Text>
                  )}
                </FormControl>
              </>
            )}

            <FormControl>
              <FormLabel fontSize="sm" mb={1}>Observação</FormLabel>
              <Textarea size="sm" rows={3} placeholder="O que ficou desta conversa..." value={obs} onChange={(e) => setObs(e.target.value)} />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancelar</Button>
          <Button
            size="sm" bg={ACCENT} color="white" _hover={{ bg: "#d4702a" }}
            onClick={salvar} isLoading={salvando} isDisabled={!podeSalvar}
          >
            Registrar desfecho
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
